import { useState } from "react";
import ChartTooltip from "./ChartTooltip";

function cumulative(arr) {
  let s = 0;
  return arr.map((v) => {
    s += v;
    return s;
  });
}

export default function SignupComparisonChart({ series, equivalent, height = 320, compact = false }) {
  const [tip, setTip] = useState(null);

  if (!series?.labels?.length) {
    return <div className="text-caption" style={{ padding: 24 }}>No sign-up data for this month yet.</div>;
  }

  const curCum = cumulative(series.current);
  const prevCum = cumulative(series.previous);
  const max = Math.max(1, ...curCum, ...prevCum);
  const w = 800;
  const h = height - 80;
  const step = w / Math.max(1, series.labels.length - 1);

  const path = (vals) =>
    vals
      .map((v, i) => {
        const x = i * step;
        const y = h - (v / max) * (h - 12) - 6;
        return `${i === 0 ? "M" : "L"}${x.toFixed(1)},${y.toFixed(1)}`;
      })
      .join(" ");

  const eq = equivalent;

  const showTip = (i, clientX, clientY, hostRect) => {
    const day = series.labels[i];
    setTip({
      x: clientX - hostRect.left,
      y: clientY - hostRect.top,
      placement: "above",
      title: `Day ${day}`,
      lines: [
        `This month: ${series.current[i]} daily · ${curCum[i]} cumulative`,
        `Last month: ${series.previous[i]} daily · ${prevCum[i]} cumulative`,
      ],
    });
  };

  return (
    <div
      className={`signup-comparison-chart${compact ? " signup-comparison-chart--compact" : ""}`}
      style={{ position: "relative" }}
    >
      <div className="signup-comparison-head">
        <div>
          <div className="signup-comparison-value">{eq?.current ?? "—"}</div>
          <div className="signup-comparison-label">Sign-ups · same period vs last month</div>
          {eq && (
            <div className="signup-comparison-meta">
              <span className={eq.growthPct >= 0 ? "text-up" : "text-down"}>{eq.growthLabel}</span>
              <span className="signup-comparison-vs">{eq.comparisonText}</span>
            </div>
          )}
        </div>
      </div>
      <div className="signup-comparison-legend">
        <span><i className="legend-line legend-line--current" /> This month (cumulative)</span>
        <span><i className="legend-line legend-line--prev" /> Same days last month</span>
      </div>
      <svg viewBox={`0 0 ${w} ${height}`} className="signup-comparison-svg" role="img" aria-label="Sign-up comparison">
        {series.labels.map((lbl, i) => (
          <text key={lbl} x={i * step} y={height - 8} fontSize="10" fill="var(--text-3)">
            {lbl}
          </text>
        ))}
        <path d={path(prevCum)} fill="none" stroke="var(--text-3)" strokeWidth="2" strokeDasharray="5 4" opacity="0.85" />
        <path d={path(curCum)} fill="none" stroke="var(--chart-line)" strokeWidth="2.5" />
        {series.labels.map((lbl, i) => {
          const x = i * step;
          const yCur = h - (curCum[i] / max) * (h - 12) - 6;
          const yPrev = h - (prevCum[i] / max) * (h - 12) - 6;
          return (
            <g key={`hit-${lbl}`}>
              <circle
                cx={x}
                cy={yCur}
                r={10}
                fill="transparent"
                style={{ cursor: "crosshair" }}
                onMouseEnter={(e) => {
                  const host = e.currentTarget.closest(".signup-comparison-chart");
                  if (!host) return;
                  showTip(i, e.clientX, e.clientY, host.getBoundingClientRect());
                }}
                onMouseMove={(e) => {
                  const host = e.currentTarget.closest(".signup-comparison-chart");
                  if (!host) return;
                  showTip(i, e.clientX, e.clientY, host.getBoundingClientRect());
                }}
                onMouseLeave={() => setTip(null)}
              />
              <circle cx={x} cy={yCur} r={3.5} fill="var(--chart-line)" className="signup-comparison-dot" />
              <circle cx={x} cy={yPrev} r={3} fill="var(--text-3)" className="signup-comparison-dot signup-comparison-dot--prev" />
            </g>
          );
        })}
      </svg>
      <ChartTooltip tip={tip} />
    </div>
  );
}
